import { type AnyFormApi } from '@tanstack/react-form';
import { useQuestionBuilder } from 'lib-node-question-builder';
import {
  and,
  formIs,
  formIsNot,
  or,
  screenIs,
  screenIsNot,
} from 'lib-node-question-builder/utils';
import { useEffect, useState } from 'react';

import type {
  DtoV1EnumExclusionReason,
  DtoV1Option,
  DtoV1UnderwritingSummariesLiabilityLiabilitySummaryResponse,
  DtoV1UnderwritingSummariesLiabilityLiabilitySummaryType,
  DtoV1UnderwritingSummariesLiabilitySaveLiabilityRequest,
  UWMCLibraryCommonDomainEnumsLoanProductType,
  UWMCLibraryCommonDomainEnumsLoanPurposeType,
  UWMCLibraryCommonDomainEnumsRefiPurposeType,
} from '~/__generated__/api';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import type { ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import useUrl from '~/common/hooks/useUrl';
import { useVAIRRRLCheck } from '~/common/hooks/useVAIRRRLCheck';
import {
  EXCLUSION_REASON_CODES,
  LIABILITY_TYPE_CODES,
} from '~/pages/Liabilities/constants';
import { useGetRealEstateOwnedSummaryQuery } from '~/pages/Loan1003DataGrids/RealEstateOwned/hooks';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';
import ScrollIntoViewContainer from '~/pages/Loan1003DataGrids/common/components/ScrollIntoViewContainer';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import {
  type LiabilityFormData,
  use1003LiabilitiesFormData,
  useSaveLiabilityMutation,
} from '../hooks';
import { useLiability1003Store } from '../liability1003Store';
import {
  calculateRemainingTerm,
  getRawValue,
  sanitizeInterestRate,
} from '../utils';

type AutoExcludeResult = {
  excludePayment: boolean;
  exclusionReason: string;
} | null;

const ORIGINAL_ACCOUNT_TYPES = [
  LIABILITY_TYPE_CODES.REAL_ESTATE,
  LIABILITY_TYPE_CODES.HELOC,
] as const;
const MAX_REMAINING_TERM = 999;
const VA_LOAN_PRODUCT: UWMCLibraryCommonDomainEnumsLoanProductType = 2;
const VA_FIRST_MORTGAGE_PURPOSE: UWMCLibraryCommonDomainEnumsLoanPurposeType = 3;
const VA_SECOND_MORTGAGE_PURPOSE: UWMCLibraryCommonDomainEnumsLoanPurposeType = 10;
const VA_REFINANCE_PURPOSE = {
  IRRRL: 7,
  PATRIOT_PLUS: 8,
  CASH_OUT_TYPE_I: 9,
  CASH_OUT_TYPE_II: 10,
} as const satisfies Record<
  'IRRRL' | 'PATRIOT_PLUS' | 'CASH_OUT_TYPE_I' | 'CASH_OUT_TYPE_II',
  UWMCLibraryCommonDomainEnumsRefiPurposeType
>;
const VA_REFINANCE_PURPOSES =
  new Set<UWMCLibraryCommonDomainEnumsRefiPurposeType>(
    Object.values(VA_REFINANCE_PURPOSE),
  );

const LIABILITY_SUMMARY_TYPE_VALUES = new Set<number>(
  Object.values(LIABILITY_TYPE_CODES),
);
const EXCLUSION_REASON_VALUES = new Set<number>(
  Object.values(EXCLUSION_REASON_CODES),
);

function toNumberOrNull(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const numericValue = Number.parseFloat(value.replaceAll(',', ''));

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  return null;
}

function toLiabilitySummaryTypeOrNull(value: unknown) {
  const parsedValue = toNumberOrNull(value);

  if (parsedValue === null || !LIABILITY_SUMMARY_TYPE_VALUES.has(parsedValue)) {
    return null;
  }

  return parsedValue as DtoV1UnderwritingSummariesLiabilityLiabilitySummaryType;
}

function toExclusionReasonOrNull(value: unknown) {
  const parsedValue = toNumberOrNull(value);

  if (parsedValue === null || !EXCLUSION_REASON_VALUES.has(parsedValue)) {
    return null;
  }

  return parsedValue as DtoV1EnumExclusionReason;
}

function shouldShowOriginalAccountInfo(liabilitySummaryType: unknown) {
  const numericLiabilitySummaryType = toNumberOrNull(liabilitySummaryType);

  if (numericLiabilitySummaryType === null) {
    return false;
  }

  return ORIGINAL_ACCOUNT_TYPES.includes(numericLiabilitySummaryType);
}

function shouldShowPresentPAndI({
  liabilitySummaryType,
  lienPosition,
  payoffRequired,
  associatedRealEstate,
  subjectPropertyAddressId,
  vaSummaryData,
  isVaIrrrl,
}: {
  liabilitySummaryType: unknown;
  lienPosition: unknown;
  payoffRequired: unknown;
  associatedRealEstate: unknown;
  subjectPropertyAddressId: number | undefined;
  vaSummaryData?: DtoV1UnderwritingSummariesLiabilityLiabilitySummaryResponse['vaSummaryData'];
  isVaIrrrl: boolean;
}) {
  if (isVaIrrrl && vaSummaryData?.isUwmToUwm) {
    return true;
  }

  return (
    vaSummaryData?.loanProduct === VA_LOAN_PRODUCT &&
    (vaSummaryData.loanPurpose === VA_FIRST_MORTGAGE_PURPOSE ||
      vaSummaryData.loanPurpose === VA_SECOND_MORTGAGE_PURPOSE) &&
    VA_REFINANCE_PURPOSES.has(vaSummaryData.refinancePurpose) &&
    toNumberOrNull(liabilitySummaryType) === LIABILITY_TYPE_CODES.REAL_ESTATE &&
    toNumberOrNull(lienPosition) === 0 &&
    payoffRequired === true &&
    toNumberOrNull(associatedRealEstate) === subjectPropertyAddressId
  );
}

function getExclusionReasonOptions(
  options: DtoV1Option[] | null | undefined,
  liabilitySummaryType: unknown,
) {
  const allOptions = options ?? [];
  const liabilityTypeValue =
    typeof liabilitySummaryType === 'string' ||
    typeof liabilitySummaryType === 'number'
      ? String(liabilitySummaryType)
      : null;

  if (liabilityTypeValue !== LIABILITY_TYPE_CODES.REVOLVING.toString()) {
    return allOptions;
  }

  return allOptions.filter(
    (option) =>
      String(option.value ?? '') !==
      EXCLUSION_REASON_CODES.LESS_THAN_10_MONTHS_REMAIN.toString(),
  );
}

function autoExcludePayment({
  currentLiabilityType,
  nextLiabilityType,
  exclusionReason,
  remainingTerm,
  isLoanFha,
}: {
  currentLiabilityType: unknown;
  nextLiabilityType: unknown;
  exclusionReason: unknown;
  remainingTerm: unknown;
  isLoanFha: boolean;
}): AutoExcludeResult {
  const currentType = toNumberOrNull(currentLiabilityType);
  const nextType = toNumberOrNull(nextLiabilityType);
  const exclusionReasonCode = toNumberOrNull(exclusionReason);
  const remainingTermValue = toNumberOrNull(remainingTerm);

  const isInstallment = (type: number | null) =>
    type === LIABILITY_TYPE_CODES.INSTALLMENT;
  const isLessThan10Months = (reason: number | null) =>
    reason === EXCLUSION_REASON_CODES.LESS_THAN_10_MONTHS_REMAIN;

  const wasInstallment = isInstallment(currentType);
  const willBeInstallment = isInstallment(nextType);
  const exclusionIsLessThan10 = isLessThan10Months(exclusionReasonCode);

  if (
    (wasInstallment && !willBeInstallment && exclusionIsLessThan10) ||
    (!wasInstallment &&
      willBeInstallment &&
      exclusionIsLessThan10 &&
      remainingTermValue !== null &&
      remainingTermValue > 10)
  ) {
    return {
      excludePayment: false,
      exclusionReason: '',
    };
  }

  if (
    !wasInstallment &&
    willBeInstallment &&
    !exclusionIsLessThan10 &&
    remainingTermValue !== null &&
    remainingTermValue > 10
  ) {
    return null;
  }

  if (
    remainingTermValue !== null &&
    remainingTermValue >= 0 &&
    remainingTermValue <= 10 &&
    willBeInstallment &&
    !isLoanFha
  ) {
    return {
      excludePayment: true,
      exclusionReason:
        EXCLUSION_REASON_CODES.LESS_THAN_10_MONTHS_REMAIN.toString(),
    };
  }

  if (
    exclusionIsLessThan10 &&
    remainingTermValue !== null &&
    remainingTermValue > 10
  ) {
    return {
      excludePayment: false,
      exclusionReason: '',
    };
  }

  return null;
}

function applyAutoExcludeValues(
  formApi: AnyFormApi,
  autoExcludeValues: AutoExcludeResult,
) {
  if (!autoExcludeValues) {
    return;
  }

  formApi.setFieldValue('excludePayment', autoExcludeValues.excludePayment);
  formApi.setFieldValue('exclusionReason', autoExcludeValues.exclusionReason);
}

function handleLiabilitySummaryTypeChange({
  formApi,
  fieldValue,
  isLoanFha,
}: {
  formApi: AnyFormApi;
  fieldValue: unknown;
  isLoanFha: boolean;
}) {
  const autoExcludeValues = autoExcludePayment({
    currentLiabilityType: formApi.state.values.liabilitySummaryType,
    nextLiabilityType: fieldValue,
    exclusionReason: formApi.state.values.exclusionReason,
    remainingTerm: formApi.state.values.remainingTerm,
    isLoanFha,
  });

  applyAutoExcludeValues(formApi, autoExcludeValues);

  if (shouldShowOriginalAccountInfo(fieldValue)) {
    return;
  }

  formApi.setFieldValue('associatedRealEstate', '');
  formApi.setFieldValue('lienPosition', '');
  formApi.setFieldValue('ownerOfMortgage', '');
  formApi.setFieldValue('isCurrentlyServicedByInstitution', false);
  formApi.setFieldValue('isCurrentlyFHAInsured', false);
}

function handlePaymentOrBalanceChange({
  formApi,
  fieldName,
  fieldValue,
  isLoanFha,
}: {
  formApi: AnyFormApi;
  fieldName: 'payment' | 'balance';
  fieldValue: unknown;
  isLoanFha: boolean;
}) {
  const paymentRaw =
    fieldName === 'payment'
      ? getRawValue(fieldValue as number | string)
      : getRawValue(formApi.state.values.payment ?? '');

  const balanceRaw =
    fieldName === 'balance'
      ? getRawValue(fieldValue as number | string)
      : getRawValue(formApi.state.values.balance ?? '');

  const remainingTerm = calculateRemainingTerm(
    Number(balanceRaw),
    Number(paymentRaw),
    MAX_REMAINING_TERM,
  );

  const currentRemainingTerm = formApi.state.values.remainingTerm;

  if (remainingTerm !== currentRemainingTerm) {
    formApi.setFieldValue('remainingTerm', remainingTerm);
  }

  if (fieldName === 'balance' && formApi.state.values.payoffRequired) {
    formApi.setFieldValue('payoffAmount', balanceRaw);
  }

  const autoExcludeValues = autoExcludePayment({
    currentLiabilityType: formApi.state.values.liabilitySummaryType,
    nextLiabilityType: formApi.state.values.liabilitySummaryType,
    exclusionReason: formApi.state.values.exclusionReason,
    remainingTerm,
    isLoanFha,
  });

  applyAutoExcludeValues(formApi, autoExcludeValues);
}

function handleExcludePaymentChange({
  formApi,
  fieldValue,
}: {
  formApi: AnyFormApi;
  fieldValue: unknown;
}) {
  if (fieldValue !== false) {
    return;
  }

  if (
    formApi.state.values.exclusionReason ===
    EXCLUSION_REASON_CODES.TO_BE_PAID.toString()
  ) {
    formApi.setFieldValue('payoffRequired', false);
  }

  formApi.setFieldValue('exclusionReason', '');
}

function handlePayoffRequiredChange({
  formApi,
  fieldValue,
}: {
  formApi: AnyFormApi;
  fieldValue: unknown;
}) {
  const payoffAmount = fieldValue
    ? (toNumberOrNull(formApi.state.values.balance) ?? 0)
    : 0;

  formApi.setFieldValue('payoffAmount', payoffAmount);
}

function handleExclusionReasonChange({
  formApi,
  fieldValue,
}: {
  formApi: AnyFormApi;
  fieldValue: unknown;
}) {
  const nextExclusionReason = toNumberOrNull(fieldValue);

  if (nextExclusionReason === EXCLUSION_REASON_CODES.TO_BE_PAID) {
    if (!formApi.state.values.payoffRequired) {
      formApi.setFieldValue('payoffRequired', true);
    }

    return;
  }

  if (
    formApi.state.values.excludePayment &&
    formApi.state.values.payoffRequired
  ) {
    formApi.setFieldValue('payoffRequired', false);
  }
}

function buildLiabilitySavePayload({
  values,
  selectedLiabilityId,
  obfuscatedLoanIdentifier,
}: {
  values: LiabilityFormData;
  selectedLiabilityId: number;
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier;
}): DtoV1UnderwritingSummariesLiabilitySaveLiabilityRequest {
  const liabilitySummaryType = toLiabilitySummaryTypeOrNull(
    values.liabilitySummaryType,
  );
  const excludePayment = values.excludePayment ?? false;
  const payoffRequired = values.payoffRequired ?? false;
  const associatedRealEstate = toNumberOrNull(values.associatedRealEstate);
  const exclusionReason = toExclusionReasonOrNull(values.exclusionReason);

  return {
    ...values,
    liabilitySummaryType,
    excludeBalance: excludePayment,
    excludePayment,
    exclusionReason: excludePayment ? exclusionReason : null,
    liabilityIdentifier: {
      ...obfuscatedLoanIdentifier,
      debtId: selectedLiabilityId,
      debtLenderDatabaseId: 1,
    },
    addressIdentifier: shouldShowOriginalAccountInfo(liabilitySummaryType)
      ? {
          addressLdId: 1,
          addressId: associatedRealEstate ?? undefined,
        }
      : null,
    payoffAmount: payoffRequired ? toNumberOrNull(values.payoffAmount) : 0,
    customerIdentifiers: (values.customerIdentifiers ?? []).flatMap(
      (customerIdentifier) => {
        const customerRecordId = toNumberOrNull(customerIdentifier);

        if (customerRecordId === null) {
          return [];
        }

        return [
          {
            obfuscatedLenderDatabaseId:
              obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
            customerRecordId,
          },
        ];
      },
    ),
  };
}

type LiabilitiesFormProps = {
  selectedLiabilityId: number;
};

export function LiabilitiesForm({
  selectedLiabilityId,
}: Readonly<LiabilitiesFormProps>) {
  const { isReadOnly, obfuscatedLoanIdentifier } = useUrl();
  const loanProductType = useGetLoanSummary().data?.loanInfo?.loanProductType;
  const isLoanFha = loanProductType === 'FHA';
  const isVaIrrrl = useVAIRRRLCheck();
  const { data: realEstateOwnedData } = useGetRealEstateOwnedSummaryQuery();
  const { mutate: saveLiability, isLoading: isSaving } =
    useSaveLiabilityMutation();

  const { data, isLoading } = use1003LiabilitiesFormData(selectedLiabilityId);
  const subjectPropertyAddressId = realEstateOwnedData?.realEstateOwned?.find(
    (property) => property.isSubjectProperty,
  )?.addressId?.addressId;

  const isWriteAccessDisabled =
    useIsWriteAccessDisabled() || isReadOnly || !data?.hasWritePermission;
  const isReadOnlyUwmToUwm = Boolean(
    data?.vaSummaryData?.isUwmToUwm && isVaIrrrl,
  );
  const readOnlyOriginalAccountCondition = or(
    READONLY_CONDITION,
    screenIs('isReadOnlyUwmToUwm', true),
  );

  const [exclusionReasons, setExclusionReasons] = useState(
    data?.dropdownOptions?.availableExclusionReasons ?? [],
  );

  useEffect(() => {
    setExclusionReasons(
      getExclusionReasonOptions(
        data?.dropdownOptions?.availableExclusionReasons,
        data?.formData?.liabilitySummaryType,
      ),
    );
  }, [
    data?.dropdownOptions?.availableExclusionReasons,
    data?.formData?.liabilitySummaryType,
  ]);

  const { form, handleSubmit } = useQuestionBuilder({
    formData: data?.formData,
    screenData: {
      isWriteAccessDisabled,
      isReadOnlyUwmToUwm,
    },
    hooks: {
      onSubmit: (values) => {
        if (!obfuscatedLoanIdentifier) {
          return;
        }

        const savePayload = buildLiabilitySavePayload({
          values,
          selectedLiabilityId,
          obfuscatedLoanIdentifier,
        });

        saveLiability(savePayload);
      },
    },
  });

  return (
    <ScrollIntoViewContainer valueToWatch>
      <form.Provider>
        <Loan1003FormContainer
          isLoading={isLoading || isSaving}
          readOnlyMode={isWriteAccessDisabled}
          onCloseClicked={() => {
            useLiability1003Store.setState({ selectedLiabilityId: null });
          }}
          onSaveClicked={() => {
            handleSubmit();
          }}
        >
          <Loan1003FormSection title="Liability Information">
            <form.CheckboxGroup
              name="customerIdentifiers"
              label="Associated Borrower(s)"
              rules={{
                required:
                  'There must be at least one name associated with the liability.',
              }}
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
              style={{
                paddingBottom: 0,
                paddingTop: '0.5rem',
              }}
              options={data?.borrowerOptions ?? []}
              hasBottomBorder={false}
            />
            <form.Grid name="liability form grid" columns={4}>
              <form.GridItem>
                <form.Select
                  name="liabilitySummaryType"
                  label="Liability Type"
                  placeholder="Select Liability Type"
                  rules={{ required: true }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                  options={data?.dropdownOptions?.availableLiabilityTypes ?? []}
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      setExclusionReasons(
                        getExclusionReasonOptions(
                          data?.dropdownOptions?.availableExclusionReasons,
                          value,
                        ),
                      );

                      handleLiabilitySummaryTypeChange({
                        formApi: fieldApi.form,
                        fieldValue: value,
                        isLoanFha,
                      });
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="creditorName"
                  label="Creditor Name"
                  rules={{ required: true }}
                  conditions={{ isDisabled: READONLY_CONDITION }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="accountNumber"
                  label="Account Number"
                  conditions={{ isDisabled: READONLY_CONDITION }}
                />
              </form.GridItem>
              <form.GridItem rowStart={2}>
                <form.Currency
                  name="payment"
                  label="Monthly Payment"
                  rules={{ required: true }}
                  conditions={{ isDisabled: READONLY_CONDITION }}
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      handlePaymentOrBalanceChange({
                        formApi: fieldApi.form,
                        fieldName: fieldApi.name,
                        fieldValue: value,
                        isLoanFha,
                      });
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem rowStart={2}>
                <form.Currency
                  name="balance"
                  label="Balance"
                  rules={{ required: true }}
                  conditions={{ isDisabled: READONLY_CONDITION }}
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      handlePaymentOrBalanceChange({
                        formApi: fieldApi.form,
                        fieldName: fieldApi.name,
                        fieldValue: value,
                        isLoanFha,
                      });
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem rowStart={2}>
                <form.Number
                  name="remainingTerm"
                  label="Remaining Term"
                  conditions={{ isDisabled: true }}
                />
              </form.GridItem>
              <form.GridItem rowStart={2}>
                <form.Currency
                  name="payoffAmount"
                  label="Payoff Amount"
                  conditions={{
                    isDisabled: true,
                  }}
                />
              </form.GridItem>
              <form.GridItem rowStart={3}>
                <form.Checkbox
                  name="excludePayment"
                  label="Exclude Payment/Balance"
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      handleExcludePaymentChange({
                        formApi: fieldApi.form,
                        fieldValue: value,
                      });
                    },
                  }}
                  conditions={{ isDisabled: READONLY_CONDITION }}
                />
              </form.GridItem>
              <form.GridItem rowStart={3} colStart={4}>
                <form.Checkbox
                  name="payoffRequired"
                  label="Payoff Required"
                  conditions={{
                    isDisabled: or(
                      READONLY_CONDITION,
                      formIs(
                        'exclusionReason',
                        EXCLUSION_REASON_CODES.TO_BE_PAID.toString(),
                      ),
                      formIs(
                        'exclusionReason',
                        EXCLUSION_REASON_CODES.TO_BE_PAID,
                      ),
                    ),
                  }}
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      handlePayoffRequiredChange({
                        formApi: fieldApi.form,
                        fieldValue: value,
                      });
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem rowStart={4}>
                <form.Select
                  name="exclusionReason"
                  label="Exclusion Reason"
                  placeholder="Select Exclusion Reason"
                  conditions={{
                    isRequired: and(
                      screenIsNot('isWriteAccessDisabled', true),
                      formIs('excludePayment', true),
                    ),
                    isDisabled: or(
                      READONLY_CONDITION,
                      formIsNot('excludePayment', true),
                    ),
                  }}
                  listeners={{
                    onChange: ({ value, fieldApi }) => {
                      handleExclusionReasonChange({
                        formApi: fieldApi.form,
                        fieldValue: value,
                      });
                    },
                  }}
                  options={exclusionReasons}
                />
              </form.GridItem>
            </form.Grid>
          </Loan1003FormSection>
          <form.Subscribe
            selector={(s) =>
              shouldShowOriginalAccountInfo(s.values.liabilitySummaryType)
            }
          >
            {(shouldShow) =>
              shouldShow && (
                <Loan1003FormSection title="Original Account Information">
                  <form.Grid
                    name="original account information grid"
                    columns={4}
                  >
                    <form.GridItem colSpan={2}>
                      <form.Select
                        name="associatedRealEstate"
                        label="Associated Real Estate"
                        placeholder="Select Option"
                        rules={{ required: true }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                        options={
                          data?.dropdownOptions
                            ?.availableRealEstateProperties ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem>
                      <form.Select
                        name="lienPosition"
                        label="Lien Position"
                        placeholder="Select Option"
                        rules={{ required: true }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                        options={
                          data?.dropdownOptions?.availableLienPositions ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem>
                      <form.Select
                        name="ownerOfMortgage"
                        label="Owner of Mortgage"
                        placeholder="Select Option"
                        rules={{ required: true }}
                        conditions={{ isDisabled: READONLY_CONDITION }}
                        options={
                          data?.dropdownOptions?.availableOwnersOfMortgage ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={2} colStart={1}>
                      <form.Checkbox
                        name="isCurrentlyServicedByInstitution"
                        label="Loan currently serviced by your institution"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={2} colStart={2}>
                      <form.Checkbox
                        name="isCurrentlyFHAInsured"
                        label="Original loan is currently FHA insured"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={3} colStart={1}>
                      <form.Date
                        name="creditReportOpenedDate"
                        label="Credit Report Opened Date"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={3} colStart={2}>
                      <form.Select
                        name="originalMortgageType"
                        label="Original Mortgage Type"
                        placeholder="Select Option"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        options={
                          data?.dropdownOptions
                            ?.availableOriginalMortgageType ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={3} colStart={3}>
                      <form.Select
                        name="originalMortgageDesc"
                        label="Original Mortgage Desc"
                        placeholder="Select Option"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        options={
                          data?.dropdownOptions
                            ?.availableOriginalMortgageDesc ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={3} colStart={4}>
                      <form.Select
                        name="originalMortgageLoanPurpose"
                        label="Original Mortgage Purpose"
                        placeholder="Select Option"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        options={
                          data?.dropdownOptions
                            ?.availableOriginalMortgageLoanPurpose ?? []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={4} colStart={1}>
                      <form.Currency
                        name="originalSalesPrice"
                        label="Original Sales Price"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={4} colStart={2}>
                      <form.Currency
                        name="mostRecentAppraisedValue"
                        label="Most Recent Appraised Value"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={4} colStart={3}>
                      <form.Currency
                        name="highestBalance"
                        label="Highest Balance"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={5} colStart={1}>
                      <form.Number
                        name="originalTerm"
                        label="Original Term (Months)"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={5} colStart={2}>
                      <form.Date
                        name="originalFirstPaymentDate"
                        label="Original First Payment Date"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={5} colStart={3}>
                      <form.Percentage
                        name="originalInterestRate"
                        label="Original Interest Rate"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        listeners={{
                          onBlur: ({ fieldApi, value }) => {
                            if (
                              value.toString() !== sanitizeInterestRate(value)
                            ) {
                              fieldApi.setValue(
                                Number.parseFloat(sanitizeInterestRate(value)),
                              );
                            }
                          },
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={5} colStart={4}>
                      <form.Percentage
                        name="originalApr"
                        label="Original APR"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        listeners={{
                          onBlur: ({ fieldApi, value }) => {
                            if (
                              value.toString() !== sanitizeInterestRate(value)
                            ) {
                              fieldApi.setValue(
                                Number.parseFloat(sanitizeInterestRate(value)),
                              );
                            }
                          },
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={6} colStart={1}>
                      <form.Number
                        name="originalTermRemaining"
                        label="Original Term Remaining (Months)"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={6} colStart={2}>
                      <form.Currency
                        name="originalMonthlyMiAmount"
                        label="Original Monthly MI Amt"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={6} colStart={3}>
                      <form.Select
                        name="originalSectionAct"
                        label="Original Section of Act"
                        placeholder="Select Option"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                        options={
                          data?.dropdownOptions?.availableOriginalSectionAct ??
                          []
                        }
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={7} colStart={1}>
                      <form.Date
                        name="originalNextAdjustment"
                        label="Original Next Adjust Date"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.GridItem rowStart={7} colStart={2}>
                      <form.Number
                        name="numberOfPaymentsMade"
                        label="Number of Payments Made"
                        conditions={{
                          isDisabled: readOnlyOriginalAccountCondition,
                        }}
                      />
                    </form.GridItem>
                    <form.Subscribe
                      selector={(state) =>
                        shouldShowPresentPAndI({
                          liabilitySummaryType:
                            state.values.liabilitySummaryType,
                          lienPosition: state.values.lienPosition,
                          payoffRequired: state.values.payoffRequired,
                          associatedRealEstate:
                            state.values.associatedRealEstate,
                          subjectPropertyAddressId,
                          vaSummaryData: data?.vaSummaryData,
                          isVaIrrrl,
                        })
                      }
                    >
                      {(shouldShow) =>
                        shouldShow && (
                          <form.GridItem rowStart={7} colStart={3}>
                            <form.Currency
                              name="presentPAndI"
                              label="Present P & I"
                              conditions={{
                                isDisabled: readOnlyOriginalAccountCondition,
                              }}
                            />
                          </form.GridItem>
                        )
                      }
                    </form.Subscribe>
                  </form.Grid>
                </Loan1003FormSection>
              )
            }
          </form.Subscribe>
        </Loan1003FormContainer>
      </form.Provider>
    </ScrollIntoViewContainer>
  );
}
